import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Settings as SettingsIcon, Inbox as InboxIcon } from "lucide-react";
import PropTypes from "prop-types";
import { clearAdminToken } from "@/lib/api";

export default function AdminShell({ children, title, action }) {
  const navigate = useNavigate();
  const location = useLocation();
  const onInbox = location.pathname.startsWith("/admin/inbox");
  const onSettings = location.pathname.startsWith("/admin/settings");

  const logout = () => {
    clearAdminToken();
    navigate("/admin", { replace: true });
  };

  return (
    <div data-testid="admin-shell" className="min-h-screen" style={{ backgroundColor: "var(--wt-bg)" }}>
      <header
        className="border-b sticky top-0 z-30"
        style={{
          borderColor: "var(--wt-border)",
          backgroundColor: "rgba(250,247,240,0.94)",
          backdropFilter: "saturate(140%) blur(8px)",
        }}
      >
        <div className="wt-container h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="admin-brand-link" className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md font-serif"
              style={{
                backgroundColor: "var(--wt-primary)",
                color: "var(--wt-gold)",
                boxShadow: "inset 0 -2px 0 var(--wt-gold)",
              }}
            >
              W
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-serif text-[15px] font-semibold wt-primary">Wanakee Trask</span>
              <span className="text-[10px] uppercase tracking-[0.18em] wt-text-muted">
                Admin Console
              </span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1" aria-label="Admin navigation">
            <Link
              to="/admin/inbox"
              data-testid="admin-nav-inbox"
              className="inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md font-semibold"
              style={{
                backgroundColor: onInbox ? "var(--wt-primary)" : "transparent",
                color: onInbox ? "var(--wt-bg)" : "var(--wt-primary)",
              }}
            >
              <InboxIcon size={14} /> Inbox
            </Link>
            <Link
              to="/admin/settings"
              data-testid="admin-nav-settings"
              className="inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md font-semibold"
              style={{
                backgroundColor: onSettings ? "var(--wt-primary)" : "transparent",
                color: onSettings ? "var(--wt-bg)" : "var(--wt-primary)",
              }}
            >
              <SettingsIcon size={14} /> Settings
            </Link>
          </nav>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md"
            style={{ border: "1px solid rgba(6,78,59,0.2)", color: "var(--wt-primary)" }}
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
        {/* Mobile sub-nav */}
        <div
          className="md:hidden border-t"
          style={{ borderColor: "var(--wt-border)", backgroundColor: "#fff" }}
        >
          <div className="wt-container py-2 flex gap-2">
            <Link to="/admin/inbox" className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm h-9 rounded-md font-semibold" style={{ backgroundColor: onInbox ? "var(--wt-primary)" : "transparent", color: onInbox ? "var(--wt-bg)" : "var(--wt-primary)" }}>
              <InboxIcon size={14} /> Inbox
            </Link>
            <Link to="/admin/settings" className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm h-9 rounded-md font-semibold" style={{ backgroundColor: onSettings ? "var(--wt-primary)" : "transparent", color: onSettings ? "var(--wt-bg)" : "var(--wt-primary)" }}>
              <SettingsIcon size={14} /> Settings
            </Link>
          </div>
        </div>
      </header>
      <section className="wt-container py-8">
        {(title || action) && (
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              {title && <h1 className="wt-h2">{title}</h1>}
            </div>
            {action}
          </div>
        )}
        {children}
      </section>
    </div>
  );
}

AdminShell.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  action: PropTypes.node,
};
